import api from '../utils/api.js';

const state = {
	clients: [],

	activeClient: {
		ad_accounts: [],
		team_members: [],
		ga_views: [],
		social_profiles: [],
		call_tracking_platforms: []
	},

	importing: [],

	activeAdAccount: {
		campaigns: [],
		conversions: [],
	},

	activeView: {},

	activeCallPlatform: {
		campaigns: []
	},

	activeBudgetGroup: {
		campaigns: [],
		start: '',
		end: '',
	},

	auth: {
		role: 'member'
	},

	currentTeam: {},

	isAdmin: false,

	showClientProfile: false,

	showAccountDetails: false,

	view: {
		active: 'list',
		name: ''
	}
};

const getters = {
	view: state => state.view,
	clients: state => state.clients,
	activeClient: state => state.activeClient,
	activeAdAccount: state => state.activeAdAccount,
	activeView: state => state.activeView,
	activeCallPlatform: state => state.activeCallPlatform,
	activeBudgetGroup: state => state.activeBudgetGroup,
	auth: state => state.auth,
	isAdmin: state => state.isAdmin,
	showClientProfile: state => state.showClientProfile,
	showAccountDetails: state => state.showAccountDetails,
	currentTeam: state => state.currentTeam,
	importing: state => state.importing
};

const actions = {
	getAllClients(context) {
		return api.get('/api/clients').then((response) => context.commit('GET_CLIENTS', response)).catch((error) => console.log(error));
	},

	addClient(context, data) {
		return new Promise((resolve, reject) => {
			api.post('/api/clients', data.form).then((response) => {
				context.dispatch('getAllClients');
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	setAuth(context, data) {
		context.commit('SET_AUTH', data.auth);
		context.commit('SET_IS_ADMIN', data.auth.role !== 'member');
	},

	setCurrentTeam(context, data) {
		context.commit('SET_CURRENT_TEAM', data.team);
	},

	showClientListView(context) {
		context.commit('SET_VIEW', {
			active: 'list',
			name: 'list',
		});
		context.commit('SET_ACTIVE_CLIENT', {
			ad_accounts: [],
			team_members: [],
			ga_views: [],
			social_profiles: [],
			call_tracking_platforms: []
		});
	},

	showClientProfile(context) {
		context.commit('SET_VIEW', {
			active: 'profile',
			name: 'profile',
		});
	},

	getClientInfo(context, data) {
		return new Promise((resolve, reject) => {
			api.get(`/api/clients/${data.client}`).then((response) => {
				context.commit('SET_ACTIVE_CLIENT', response);
				resolve();
			}).catch((error) => reject(error));
		});
	},

	updateClient(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/clients/${context.state.activeClient.slug}`, data.form).then((response) => {
				context.dispatch('getAllClients');
				context.dispatch('getClientInfo', {client: response.client.slug});
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	deleteClient(context, data) {
		return new Promise((resolve, reject) => {
			api.delete(`/api/clients/${context.state.activeClient.slug}`).then((response) => {
				context.dispatch('getAllClients');
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	addBudgetGroup(context, data) {
		return new Promise((resolve, reject) => {
			api.post('/api/budget-groups', data.budgetGroup).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	showBudgetGroupDetails(context, data) {
		let group = context.state.activeClient.budget_groups.find(group => group.id == data.group.id);
		context.commit('SET_VIEW', {
			active: 'budget_group_details',
			name: group.name
		});
		context.commit('SET_ACTIVE_BUDGET_GROUP', group);
	},

	updateBudgetGroup(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/budget-groups/${data.budgetGroup.id}`, data.budgetGroup).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	deleteBudgetGroup(context, data) {
		return new Promise((resolve, reject) => {
			api.delete(`/api/budget-groups/${data.budgetGroup.id}`).then((response) => {
				context.commit('DELETE_BUDGET_GROUP', data.budgetGroup);
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	removeCampaignFromBudgetGroup(context, data) {
		return new Promise((resolve, reject) => {
			api.delete(`/api/budget-groups/${context.state.activeBudgetGroup.id}/campaigns/${data.campaign.campaign_id}`).then((response) => {
				context.commit('DELETE_CAMPAIGN_FROM_BUDGET_GROUP', {
					campaign: data.campaign
				});
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	addAdAccount(context, data) {
		return new Promise((resolve, reject) => {
			api.post('/api/ad-accounts', data.adAccount).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	addCampaign(context, data) {
		return new Promise((resolve, reject) => {
			api.post(`/api/ad-accounts/${state.activeAdAccount.account_id}/campaigns`, data.campaign).then((response) => {
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	updateAccountStatus(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/ad-accounts/${data.account.account_id}`, data.account).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	deleteAdAccount(context, data) {
		return new Promise((resolve, reject) => {
			api.delete(`/api/ad-accounts/${data.account.account_id}`).then((response) => {
				context.commit('DELETE_AD_ACCOUNT', data.account);
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	showAccountDetails(context, data) {
		let account = context.state.activeClient.ad_accounts.find(account => account.account_id === data.account.account_id);
		context.commit('SET_VIEW', {
			active: 'ad_account_details',
			name: account.vendor
		});
		context.commit('SET_ACTIVE_AD_ACCOUNT', account);
	},

	hideAccountsDetails(context) {
		context.dispatch('showClientProfile');
		context.commit('SET_ACTIVE_AD_ACCOUNT', {client_name: ''});
	},

	getCampaignList(context, data) {
		return new Promise((resolve, reject) => {
			api.get(`/api/ad-accounts/${state.activeAdAccount.account_id}/campaigns?filter=${data.filter}`).then((data) => {
				context.commit('SET_CAMPAIGN_LIST', data);
				resolve(data);
			}).catch((error) => reject(error));
		});
	},

	updateCampaign(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/campaigns/${data.campaign.campaign_id}`, data.campaign).then((response) => {
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	getConversionList(context, data) {
		return new Promise((resolve, reject) => {
			api.get(`/api/ad-accounts/${state.activeAdAccount.account_id}/conversions`).then((data) => {
				context.commit('SET_CONVERSION_LIST', data);
				resolve(data);
			}).catch((error) => reject(error));
		});
	},

	toggleConversionMonitor(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/conversions/${data.conversion.id}`, data.conversion).then((response) => {
				resolve(response);
			}).catch((error) => {
				reject(error);
			});
		});
	},

	showViewDetails(context, data) {
		let view = context.state.activeClient.ga_views.find(view => view.view_id === data.view.view_id);
		context.commit('SET_VIEW', {
			active: 'ga_view_details',
			name: view.view_name
		});
		context.commit('SET_ACTIVE_VIEW', view);
	},

	addGaView(context, data) {
		return new Promise((resolve, reject) => {
			api.post('/api/ga/view', data.view).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	updateGaView(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/ga/view/${data.view.view_id}`, data.view).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	updateGaViewGoals(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/ga/view/goals`, data).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	deleteGaView(context, data) {
		return new Promise((resolve, reject) => {
			api.delete(`/api/ga/view/${data.view.view_id}`).then((response) => {
				context.commit('DELETE_GA_VIEW', data.view);
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	addSocialProfile(context, data) {
		return new Promise((resolve, reject) => {
			api.post('/api/social-profiles/', data.profile).then((response) => {
				context.dispatch('getClientInfo', {client: context.state.activeClient.slug});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	deleteProfile(context, data) {
		return new Promise((resolve, reject) => {
			api.delete(`/api/social-profiles/${data.profile.profile_id}`).then((response) => {
				context.commit('DELETE_SOCIAL_PROFILE', data.profile);
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	showCallPlatformDetails(context, data) {
		let platform = context.state.activeClient.call_tracking_platforms.find(platform => platform.account_id === data.platform.account_id);
		context.commit('SET_VIEW', {
			active: 'call_platform_details',
			name: platform.platform
		});
		context.commit('SET_ACTIVE_PLATFORM', platform);
	},

	addCallTrackingPlatform(context, data) {
		return new Promise((resolve, reject) => {
		api.post(`/api/call-tracking-platforms/`, data.platform).then((response) => {
				context.dispatch('getClientInfo', {
					client: context.state.activeClient.slug
				});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	deleteCallTrackingPlatform(context, data) {
		return new Promise((resolve, reject) => {
		api.delete(`/api/call-tracking-platforms/${data.id}`).then((response) => {
				context.dispatch('getClientInfo', {
					client: context.state.activeClient.slug
				});
				resolve(response);
			}).catch((error) => reject(error));
		});
	},

	addCallTrackingCampaign(context, data) {
		return new Promise((resolve, reject) => {
			api.post(`/api/call-tracking-platforms/${data.platform.account_id}/campaigns`, data.campaign).then((response) => {
				context.dispatch('getClientInfo', {
					client: context.state.activeClient.slug
				});
				resolve();
			}).catch((error) => reject(error));
		});
	},

	updateCallTrackingCampaign(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/call-tracking-platforms/${data.platform.account_id}/campaigns/${data.id}`, data.campaign).then((response) => {
				context.dispatch('getClientInfo', {
					client: context.state.activeClient.slug
				});
				resolve();
			}).catch((error) => reject(error));
		});
	},

	deleteCallTrackingCampaign(context, data) {
		return new Promise((resolve, reject) => {
			api.delete(`/api/call-tracking-platforms/${data.platform.account_id}/campaigns/${data.campaign_id}`).then((response) => {
				context.dispatch('getClientInfo', {
					client: context.state.activeClient.slug
				});
				resolve();
			}).catch((error) => reject(error));
		});
	},

	assignMember(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/clients/${context.state.activeClient.slug}/team`, data.member).then((response) => {
				context.commit('ASSIGN_MEMBER', data.member);
				resolve();
			}).catch((error) => {
				reject(error);
			});
		});
	},

	dissociateMember(context, data) {
		return new Promise((resolve, reject) => {
			api.patch(`/api/clients/${context.state.activeClient.slug}/team/dissociate`, data.member).then((response) => {
				context.commit('DISSOCIATE_MEMBER', data.member);
				resolve();
			}).catch((error) => {
				reject(error);
			});
		});
	}
};

const mutations = {
	GET_CLIENTS(state, data) {
		state.clients = data;
	},
	SET_AUTH(state, data) {
		state.auth = data;
	},
	SET_IS_ADMIN(state, data) {
		state.isAdmin = data;
	},
	SET_CURRENT_TEAM(state, data) {
		state.currentTeam = data;
	},
	SET_VIEW(state, data) {
		state.view = data
	},
	SET_ACTIVE_CLIENT(state, data) {
		state.activeClient = data;
	},
	SET_CAMPAIGN_LIST(state, data) {
		state.activeAdAccount.campaigns = data;
	},
	ADD_CAMPAIGN(state, data) {
		state.activeAdAccount.campaigns.push(data);
	},
	SET_CONVERSION_LIST(state, data) {
		state.activeAdAccount.conversions = data;
	},
	SET_ACTIVE_AD_ACCOUNT(state, data) {
		state.activeAdAccount = data;
	},
	SET_ACTIVE_VIEW(state, data) {
		state.activeView = data;
	},
	SET_ACTIVE_PLATFORM(state, data) {
		state.activeCallPlatform = data;
	},
	SET_ACTIVE_BUDGET_GROUP(state, data) {
		state.activeBudgetGroup = data;
	},
	DELETE_CAMPAIGN_FROM_BUDGET_GROUP(state, data) {
		state.activeBudgetGroup.campaigns = state.activeBudgetGroup.campaigns.filter(campaign => campaign.campaign_id != data.campaign.campaign_id);
	},
	ASSIGN_MEMBER(state, data) {
		state.activeClient.team_members.push(data);
	},
	DISSOCIATE_MEMBER(state, data) {
		state.activeClient.team_members = state.activeClient.team_members.filter(member => member.id !== data.id);
	},
	DELETE_AD_ACCOUNT(state, account) {
        state.activeClient.ad_accounts = state.activeClient.ad_accounts.filter(a => a.account_id !== account.account_id);
    },
	DELETE_BUDGET_GROUP(state, budgetGroup) {
        state.activeClient.budget_groups = state.activeClient.budget_groups.filter(b => b.id !== budgetGroup.id);
    },
	DELETE_GA_VIEW(state, view) {
        state.activeClient.ga_views = state.activeClient.ga_views.filter(v => v.view_id !== view.view_id);
    },
	DELETE_SOCIAL_PROFILE(state, profile) {
        state.activeClient.social_profiles = state.activeClient.social_profiles.filter(s => s.profile_id !== profile.profile_id);
	},
	ADD_TO_IMPORTING(state, id) {
		state.importing.push(id);
	},
	REMOVE_FROM_IMPORTING(state, id) {
		state.importing.splice(state.importing.indexOf(id), 1);
	},
};

export default {
	namespaced : true,
	state,
	getters,
	actions,
	mutations
};



// WEBPACK FOOTER //
// ./resources/assets/js/vuex/modules/client.js